/* eslint-disable max-len */
import {
  ChevronRight,
  DocumentScannerOutlined,
  EnergySavingsLeafOutlined,
  Public,
  PublishedWithChangesOutlined,
  Timelapse,
} from "@mui/icons-material";
import { Box, Grid, GridProps, styled, SwipeableDrawer, useMediaQuery, SvgIcon } from "@mui/material";
import Modal from "@mui/material/Modal";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { NavLink, useSearchParams } from "react-router-dom";
import { ActionBtn, BlogStoryBlock, GuardianUploadBanner, ReelNumber, TableComponent, Text } from "../../components";
import { AppRoutes } from "../../config";
import ApiRoutes from "../../config/api.routes";
import { useAxios } from "../../services/http.service";
import { LandToken } from "../../types/LandToken";
import { auctionCurrentPrice, consoleError, sizing, toReadable } from "../../utils/methods";
import { formatValue } from "../../utils/formatValue";
import {
  ClickableContainer,
  ExploreLandsSchema,
  Link,
  MobileEcoBankLandsSchema,
  TransactionMobileSchema,
  TransactionsSchema,
} from "../../utils/table-schemas";
import {
  ScanMain,
  ScanTop,
  ScanTopText,
  ScanTopLink,
  ScanTopChevron,
  MobileWidgetGrid,
  MobileBankWidget,
  MobileWidgetTextBlock,
  MobileWidgetValue,
  MobileWidgetLabel,
  MarketCapIcon,
  MintPriceIcon,
  TotalTxIcon,
  Tr3MintedIcon,
  TotalEftsIcon,
  TillHalvingIcon,
} from "./components";
import HomeHero from "../../components/HomeHero";
import PopUpGraph from "../../components/LineChart";
import { initializeBlogContent } from "../SupportCenter/BlogArticle";
import { FilterBlock } from "../../components/TableView/TableComponent";
import { HALVING_THRESHOLDS, TERM_UNIT } from "../../utils/constants";
import useAuthorization from "../../contexts/useAuthorization";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

type BlogArticle = {
  title: string;
  description: string;
  Slug: string;
  image: string;
  subtitle: string;
  paragraph: string;
  id: string;
  blog?: number;
  path?: string;
};

export const convertParcelsToHectares = (parcels: number) => {
  return (parcels * 100) / 10000;
};

// Lands in the hero carousel, and rows in the Latest Lands table — kept equal so every featured
// land is also in the table.
const FEATURED_LANDS = 5;

type LandsSort = "default" | "createdAt" | "price" | "mintCap" | "score";

// A land whose term has run out and whose TR3 has all been claimed has nothing left to offer.
// Lands not yet activated (no contract start) are never spent.
const isSpent = (token: LandToken) => {
  const contractStart = token.etNft?.easementActivated?.contractStart;
  if (!contractStart) {
    return false;
  }
  const isExpired = moment(contractStart).add(token.meta?.contractPeriod, TERM_UNIT).isBefore(moment());
  if (!isExpired) {
    return false;
  }
  const total = formatValue(token.etNft?.totalTreeForEasement as any, false);
  const claimed = formatValue(token.etNft?.easementActivated?.rewardClaimed as any, false) || 0;
  return total - claimed <= 0;
};

const sortLands = (lands: LandToken[], sort: LandsSort) => {
  switch (sort) {
    case "createdAt":
      return [...lands].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    case "price":
      return [...lands].sort(
        (a, b) =>
          (Number(auctionCurrentPrice(b.etNft?.etAuction)) || 0) - (Number(auctionCurrentPrice(a.etNft?.etAuction)) || 0)
      );
    case "mintCap":
      return [...lands].sort(
        (a, b) => (Number(b?.etNft?.totalTreeForEasement) || 0) - (Number(a?.etNft?.totalTreeForEasement) || 0)
      );
    case "score":
      return [...lands].sort((a, b) => (b.ecoScore || 0) - (a.ecoScore || 0));
    default:
      return lands;
  }
};

const EcoBank = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { isGuardian, isPioneer } = useAuthorization();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(560));

  const tableRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearch] = useState<string>("");
  const [lands, setLands] = useState<LandToken[]>([]);
  const [indicatorsLoading, setIndicatorsLoading] = useState(true);
  const [scannedLands, setScannedLands] = useState(0);
  const [marketCap, setMarketCap] = useState(0);
  const [totalTrees, setTotalTrees] = useState(0);
  const [sizeToHalving, setSizeToHalving] = useState(0);
  const [totalParcels, setTotalParcels] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [searchDone, setSearchDone] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [chartType, setChartType] = useState("price");
  const [open, setOpen] = useState(false);
  const [blogArticles, setBlogArticles] = useState<{ [key: string]: BlogArticle }>({});
  const [blogLoading, setBlogLoading] = useState(true);
  const [liveLands, setLiveLands] = useState<LandToken[]>([]);
  const [liveLandsLoading, setLiveLandsLoading] = useState<boolean>(true);
  const [currentFilter, setCurrentFilter] = useState<LandsSort>("createdAt");

  // The table's order follows the sort button; the hero always takes the newest lands. Since the
  // table shows FEATURED_LANDS rows and defaults to newest first, every featured land is in it.
  const tableLands = useMemo(() => sortLands(liveLands, currentFilter), [liveLands, currentFilter]);
  const heroLands = useMemo(() => sortLands(liveLands, "createdAt").slice(0, FEATURED_LANDS), [liveLands]);

  const handleOpen = (type: string) => {
    setChartType(type); // Update the chart type
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handleMobileOpen = () => setOpen(true);

  const [searchParams] = useSearchParams();

  const fetchTokens = async () => {
    setLiveLandsLoading(true);
    // Newest first, so the 20 we get are the latest lands (the API's default order is by latest
    // auction end, which would leave sorting by creation date to a window of older lands).
    const result = await axios
      .get(`${ApiRoutes.token.list}?etNftStatus=auction,sold,ended,completed&filter=latest_land&limit=20`)
      .catch((err) => err.response);
    if (result && result.status === 200) {
      setLiveLands(result.data.filter((token: LandToken) => !isSpent(token)));
    } else {
      consoleError("Failed to fetch latest lands", { result });
    }
    setLiveLandsLoading(false);
  };

  useEffect(() => {
    fetchIndicators();
    fetchTokens();
    document.getElementsByTagName("main")[0]?.classList.add("no-overflow");
    // Fetch the blog article
    loadBlogContent();
    return () => {
      document.getElementsByTagName("main")[0]?.classList.remove("no-overflow");
    };
  }, []);

  // React to ?search= param changes (e.g. when header search navigates here while already on this page)
  useEffect(() => {
    const searchValue = searchParams.get("search");
    if (searchValue) {
      setSearch(searchValue);
      fetchLands(searchValue);
    } else {
      // Param was cleared — reset search results
      setSearch("");
      setSearchDone(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchQuery && searchError) {
      setSearchError("");
    }

    if (!searchQuery && searchDone) {
      setSearchDone(false);
    }
  }, [searchQuery]);
  const fetchLands = async (search = "") => {
    setSearchLoading(true);
    try {
      const result: LandToken[] = await axios.get(ApiRoutes.ecoBank.lands.search(search)).then((res) => res.data);
      if (search) {
        setSearchDone(true);
        window.scrollTo({ top: tableRef?.current?.offsetTop, behavior: "smooth" });
      }
      setLands(result);
    } catch (e) {
      consoleError("Failed to fetch lands", { e });
    } finally {
      setSearchLoading(false);
    }
  };

  const loadBlogContent = async () => {
    try {
      setBlogLoading(true);
      const content = await initializeBlogContent();
      setBlogArticles(content);
    } catch (error) {
      console.error("Failed to load blog content:", error);
    } finally {
      setBlogLoading(false);
    }
  };

  const fetchIndicators = async () => {
    try {
      setIndicatorsLoading(true);
      const [result] = await Promise.all([axios.get(ApiRoutes.ecoBank.indicators.root).then((res) => res.data)]);
      setScannedLands(result.scannedLands || 0);
      setTotalTransactions(result.totalTransactions || 0);
      const treeSupply = result.treeSupply || 0;
      const trees = result.tokens.reduce((acc: any, cur: any) => acc + toReadable(cur, cur !== "0", false), 0);
      const prices = result.prices.reduce((acc: any, cur: any) => acc + toReadable(cur, true, false), 0);
      const parcels = result.parcels.reduce((acc: any, cur: any) => acc + cur, 0);
      setMarketCap(prices);
      setTotalTrees(trees);
      // setTotalParcels(convertParcelsToHectares(parcels));
      setTotalParcels(treeSupply);
      const haTillNextHalving = Number(result.haTillNextHalving / 100);
      if (Number.isFinite(haTillNextHalving)) {
        setSizeToHalving(haTillNextHalving);
      } else {
        // Find the next halving threshold based on current parcels
        const nextThreshold =
          HALVING_THRESHOLDS.find((threshold) => threshold > parcels) ||
          HALVING_THRESHOLDS[HALVING_THRESHOLDS.length - 1];
        setSizeToHalving(convertParcelsToHectares(nextThreshold - parcels));
      }
    } catch (e) {
      consoleError("Failed to fetch indicators", { e });
    } finally {
      setIndicatorsLoading(false);
    }
  };

  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getHalvingChart = () => {
    const res: any[] = [];

    for (let i = 0; i < 100; i++) {
      const filled = sizeToHalving > i * 10000;
      const block = <Block key={i} sx={{ backgroundColor: filled ? "success.main" : "transparent" }} />;
      res.push(block);
    }

    return res;
  };

  return (
    <LoadingScreen loading={searchLoading} showCircularLoader loaderColor="white">
      <BackgroundDiv>
        {isGuardian || isPioneer ? (
          <GuardianUploadBanner />
        ) : isMobile ? (
          <ScanTopLink to={AppRoutes.EcoBank.path}>
            <span>Read EcoFutures white paper.</span>
            <ChevronRight aria-hidden />
          </ScanTopLink>
        ) : (
          <ScanTop>
            <ScanTopText>
              Welcome to the Eco Future.{" "}
              <Link
                sx={{
                  display: { xs: "flex", sm: "inline" },
                  marginLeft: { xs: "0px", sm: "4px" },
                }}
                style={{
                  textDecoration: "underline",
                  textDecorationSkipInk: "none",
                  textUnderlinePosition: "from-font",
                  lineHeight: "inherit",
                  color: "#FFFFFF",
                  fontWeight: 400,
                }}
                to={AppRoutes.EcoBank.path}
              >
                Read the EcoFutures white paper.
              </Link>
            </ScanTopText>
            <ScanTopChevron to={AppRoutes.EcoBank.path} tabIndex={-1} aria-hidden>
              <ChevronRight />
            </ScanTopChevron>
          </ScanTop>
        )}
        {searchError && (
          <Text sx={{ color: "#959a80", mt: "12px", fontSize: { xs: "13px", sm: "14px" }, px: "20px" }}>
            {searchError}
          </Text>
        )}
      </BackgroundDiv>
      <ScanMain>
        {isMobile ? (
          <MobileWidgetGrid>
            <MobileBankWidget onClick={() => handleOpen("marketCap")}>
              <MarketCapIcon />
              <MobileWidgetTextBlock>
                <MobileWidgetValue>
                  {indicatorsLoading ? "..." : <ReelNumber prefix="$" value={toReadable(marketCap, false, true, 0)} />}
                </MobileWidgetValue>
                <MobileWidgetLabel>Market Cap</MobileWidgetLabel>
              </MobileWidgetTextBlock>
            </MobileBankWidget>
            <MobileBankWidget onClick={() => handleOpen("price")}>
              <MintPriceIcon />
              <MobileWidgetTextBlock>
                <MobileWidgetValue>
                  {indicatorsLoading ? (
                    "..."
                  ) : (
                    <ReelNumber prefix="$" value={toReadable(marketCap / totalTrees || 0, false, true, 4)} />
                  )}
                </MobileWidgetValue>
                <MobileWidgetLabel>Mint Price</MobileWidgetLabel>
              </MobileWidgetTextBlock>
            </MobileBankWidget>
            <MobileBankWidget onClick={() => navigate(AppRoutes.Transactions.path + "?currentFilter=all")}>
              <TotalTxIcon />
              <MobileWidgetTextBlock>
                <MobileWidgetValue>
                  {indicatorsLoading ? "..." : <ReelNumber value={toReadable(totalTransactions, false, false, 0)} />}
                </MobileWidgetValue>
                <MobileWidgetLabel>Tot Tx</MobileWidgetLabel>
              </MobileWidgetTextBlock>
            </MobileBankWidget>
            <MobileBankWidget onClick={() => navigate(AppRoutes.Lands.path + "?currentFilter=all")}>
              <TotalEftsIcon />
              <MobileWidgetTextBlock>
                <MobileWidgetValue>{indicatorsLoading ? "..." : <ReelNumber value={scannedLands} />}</MobileWidgetValue>
                <MobileWidgetLabel>Total EFTs</MobileWidgetLabel>
              </MobileWidgetTextBlock>
            </MobileBankWidget>
            <MobileBankWidget onClick={() => handleOpen("supply")}>
              <Tr3MintedIcon />
              <MobileWidgetTextBlock>
                <MobileWidgetValue>
                  {indicatorsLoading ? "..." : <ReelNumber value={toReadable(totalParcels, false, true, 2)} />}
                </MobileWidgetValue>
                <MobileWidgetLabel>TR3 Minted</MobileWidgetLabel>
              </MobileWidgetTextBlock>
            </MobileBankWidget>
            <MobileBankWidget onClick={() => handleOpen("parcels")}>
              <TillHalvingIcon />
              <MobileWidgetTextBlock>
                <MobileWidgetValue>
                  {indicatorsLoading ? (
                    "..."
                  ) : (
                    <ReelNumber suffix=" ha" value={toReadable(sizeToHalving, false, true, 2)} />
                  )}
                </MobileWidgetValue>
                <MobileWidgetLabel>Till HaLVinG</MobileWidgetLabel>
              </MobileWidgetTextBlock>
            </MobileBankWidget>
          </MobileWidgetGrid>
        ) : (
          <Grid container justifyContent="center">
            <WidgetGrid container item xs={12} sx={{ backgroundColor: "#ccc" }}>
              <BankWidget
                sx={{
                  width: "100%",
                  height: { xs: "64px", md: "72px" },
                  padding: { xs: "0 18px", md: "0 25px" },
                  display: "flex !important",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#959A80", // Updated background color from Figma (Reference: fill_3HS7A4 / fill_X0Y9OA)
                  borderTop: "none",
                  borderRadius: { xs: "20px 20px 0px 0px", md: "20px" },
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Public sx={{ width: "25px", height: "25px", color: "black" }} />
                  <Box
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      textTransform: "uppercase",
                      marginLeft: { xs: "21px", md: "20px" },
                      color: "black",
                    }}
                  >
                    Market Cap.
                  </Box>
                </Box>
                <ClickableContainer onClick={() => handleOpen("marketCap")} sx={{ justifyContent: "flex-end" }}>
                  <Box
                    className="BankWidget-value"
                    style={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      color: "black",
                      textAlign: "right",
                    }}
                  >
                    {indicatorsLoading ? (
                      "..."
                    ) : (
                      <ReelNumber
                        prefix={<span style={{ fontSize: "15px" }}>$</span>}
                        value={toReadable(marketCap, false, true, 0)}
                      />
                    )}
                  </Box>
                </ClickableContainer>
              </BankWidget>
              <BankWidget
                sx={{
                  width: "100%",
                  height: { xs: "64px", md: "72px" },
                  padding: { xs: "0 18px", md: "0 25px" },
                  display: "flex !important",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#959A80",
                  borderTop: "none",
                  position: "relative",
                  borderRadius: { xs: "0px", md: "20px" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EnergySavingsLeafOutlined sx={{ width: "25px", height: "25px", color: "black" }} />
                  <Box
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      textTransform: "uppercase",
                      marginLeft: { xs: "21px", md: "20px" },
                      color: "black",
                    }}
                  >
                    TR3 Mint Price
                  </Box>
                </Box>
                <ClickableContainer onClick={() => handleOpen("price")} sx={{ justifyContent: "flex-end" }}>
                  <Box
                    className="BankWidget-value"
                    style={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      color: "black",
                      textAlign: "right",
                    }}
                  >
                    {indicatorsLoading ? (
                      "..."
                    ) : (
                      <ReelNumber
                        prefix={<span style={{ fontSize: "15px" }}>$</span>}
                        value={toReadable(marketCap / totalTrees || 0, false, true, 4)}
                      />
                    )}
                  </Box>
                </ClickableContainer>
              </BankWidget>
              <BankWidget
                sx={{
                  width: "100%",
                  height: { xs: "64px", md: "72px" },
                  padding: { xs: "0 18px", md: "0 25px" },
                  display: "flex !important",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#959A80",
                  borderTop: "none",
                  borderRadius: { xs: "0px 0px 0px 0px", md: "20px" },
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PublishedWithChangesOutlined sx={{ width: "25px", height: "25px", color: "black" }} />
                  <Box
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      textTransform: "uppercase",
                      marginLeft: { xs: "21px", md: "20px" },
                      color: "black",
                    }}
                  >
                    Total Transactions
                  </Box>
                </Box>
                <ClickableContainer
                  onClick={() => navigate(AppRoutes.Transactions.path + "?currentFilter=all")}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Box
                    className="BankWidget-value"
                    style={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      color: "black",
                      textAlign: "right",
                    }}
                  >
                    {indicatorsLoading ? "..." : <ReelNumber value={toReadable(totalTransactions, false, false, 0)} />}
                  </Box>
                </ClickableContainer>
              </BankWidget>
              <BankWidget
                sx={{
                  width: "100%",
                  height: { xs: "64px", md: "72px" },
                  padding: { xs: "0 18px", md: "0 25px" },
                  display: "flex !important",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#959A80",
                  borderTop: "none",
                  borderRadius: { xs: "0px 0px 0px 0px", md: "20px" },
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <SvgIcon sx={{ width: "26px", height: "29px", color: "black" }} viewBox="0 0 26 29">
                    <path
                      d="M13.0002 0.228L0.2 7.1582V21.3461L13.0002 28.6727L25.8 21.3461V7.1582L13.0002 0.228ZM23.6712 20.1048L14.0688 25.6127V22.027C13.612 22.1046 13.1466 22.1562 12.6639 22.1562C12.414 22.1562 12.1725 22.1391 11.9312 22.1132V25.6127L2.32881 20.1048V9.54575L5.55275 11.4508C5.82857 10.7957 6.19903 10.1837 6.65587 9.61477L3.50138 7.76153L12.9914 2.71045L22.4818 7.76153L20.3526 9.01134V11.4938L23.6539 9.53722V20.0963L23.6712 20.1048Z"
                      fill="black"
                      stroke="black"
                      strokeWidth="0.39"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M8.88062 10.6567C7.83764 11.6997 7.312 12.9668 7.312 14.4494C7.312 15.001 7.39825 15.5354 7.57926 16.044C7.76027 16.5612 8.00169 17.0439 8.29475 17.4921L7.67396 18.1127C7.50156 18.2851 7.42395 18.4833 7.42395 18.7074C7.42395 18.9315 7.51018 19.1298 7.67396 19.3022C7.84635 19.4746 8.04455 19.5522 8.26866 19.5436C8.49277 19.535 8.69097 19.4574 8.86336 19.3022L9.45806 18.7419C9.94938 19.0609 10.4582 19.3195 10.9926 19.5177C11.527 19.716 12.087 19.8107 12.6732 19.8107C14.1557 19.8107 15.4232 19.285 16.4661 18.242C17.5091 17.1991 18.0347 15.9319 18.0347 14.4494V9.088H12.6732C11.1906 9.088 9.92359 9.61375 8.88062 10.6567ZM16.3365 14.4494C16.3365 15.4665 15.9833 16.3371 15.2679 17.0439C14.5525 17.7593 13.6903 18.1127 12.6732 18.1127C12.3198 18.1127 11.975 18.0524 11.6475 17.9317C11.3199 17.8111 10.9925 17.6731 10.6736 17.5265L13.8887 14.3114C14.0611 14.139 14.1387 13.9408 14.1387 13.7081C14.1387 13.4754 14.0524 13.2684 13.8887 13.1047C13.7163 12.9323 13.5181 12.8548 13.294 12.8548C13.0699 12.8548 12.8717 12.9409 12.6993 13.1047L9.45806 16.3456C9.32877 16.0267 9.21682 15.7078 9.13062 15.3975C9.04442 15.0872 9.00141 14.7683 9.00141 14.4494C9.00141 13.4323 9.3546 12.5617 10.07 11.8549C10.7855 11.1394 11.6476 10.786 12.6647 10.786H16.3281V14.4494H16.3365Z"
                      fill="black"
                      stroke="black"
                      strokeWidth="0.39"
                      strokeMiterlimit="10"
                    />
                  </SvgIcon>
                  <Box
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      textTransform: "uppercase",
                      marginLeft: { xs: "21px", md: "20px" },
                      color: "black",
                    }}
                  >
                    EFTs
                  </Box>
                </Box>
                <ClickableContainer
                  onClick={() => navigate(AppRoutes.Lands.path + "?currentFilter=all")}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Box
                    className="BankWidget-value"
                    style={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      color: "black",
                      textAlign: "right",
                    }}
                  >
                    {indicatorsLoading ? "..." : <ReelNumber value={scannedLands} />}
                  </Box>
                </ClickableContainer>
              </BankWidget>
              <BankWidget
                sx={{
                  width: "100%",
                  height: { xs: "64px", md: "72px" },
                  padding: { xs: "0 18px", md: "0 25px" },
                  display: "flex !important",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#959A80",
                  borderTop: "none",
                  borderRadius: { xs: "0px", md: "20px" },
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <DocumentScannerOutlined sx={{ width: "25px", height: "25px", color: "black" }} />
                  <Box
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      textTransform: "uppercase",
                      marginLeft: { xs: "21px", md: "20px" },
                      color: "black",
                    }}
                  >
                    TR3 Minted
                  </Box>
                </Box>
                <ClickableContainer onClick={() => handleOpen("supply")} sx={{ justifyContent: "flex-end" }}>
                  <Box
                    className="BankWidget-value"
                    style={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      color: "black",
                      textAlign: "right",
                    }}
                  >
                    {indicatorsLoading ? "..." : <ReelNumber value={toReadable(totalParcels, false, true, 2)} />}
                  </Box>
                </ClickableContainer>
              </BankWidget>
              <BankWidget
                sx={{
                  width: "100%",
                  height: { xs: "64px", md: "72px" },
                  padding: { xs: "0 18px", md: "0 25px" },
                  display: "flex !important",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#959A80",
                  paddingBottom: "0 !important", // Override previous paddingBottom if needed, or just let height control it
                  borderTop: "none",
                  borderRadius: { xs: "0px 0px 20px 20px", md: "20px" },
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Timelapse sx={{ width: "25px", height: "25px", color: "black" }} />
                  <Box
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      textTransform: "uppercase",
                      marginLeft: { xs: "21px", md: "20px" },
                      color: "black",
                    }}
                  >
                    Ha Till Halving
                  </Box>
                </Box>
                <ClickableContainer onClick={() => handleOpen("parcels")} sx={{ justifyContent: "flex-end" }}>
                  <Box
                    className="BankWidget-value"
                    style={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "14px",
                      lineHeight: 1.3,
                      color: "black",
                      textAlign: "right",
                    }}
                  >
                    {indicatorsLoading ? "..." : <ReelNumber value={toReadable(sizeToHalving, false, true, 2)} />}
                  </Box>
                </ClickableContainer>
              </BankWidget>
            </WidgetGrid>
          </Grid>
        )}
      </ScanMain>
      <HomeHero lands={heroLands} loading={liveLandsLoading} />
      <TableContainer ref={tableRef} style={{ backgroundColor: "#ebe1dd", borderRadius: "20px 20px 20px 20px" }}>
        <FilterBlock
          sx={{
            padding: { xs: "18px", sm: "24px" },
            "@media (min-width: 600px)": {
              display: "flex",
              overflowX: "auto",
              whiteSpace: "nowrap",
            },
            "@media (max-width: 599px)": {
              flexDirection: "row",
            },
          }}
        >
          <ActionBtn
            onClick={() => {
              setCurrentFilter("createdAt");
            }}
            disabled={currentFilter === "createdAt"}
            variant={currentFilter === "createdAt" ? "contained" : "outlined"}
            size="medium"
            sx={{
              "@media (max-width: 600px)": {
                size: "small",
                margin: 0.5,
              },
            }}
          >
            {isMobile ? "Latest Lands" : "Latest Lands"}
          </ActionBtn>
        </FilterBlock>

        <TableComponent
          mobileTitle=""
          schema={isMobile ? MobileEcoBankLandsSchema : ExploreLandsSchema}
          // apiURL={`${ApiRoutes.token.list}?etNftStatus=auction,sold`}
          apiURL=""
          equalColumnWidths={!isMobile}
          link={AppRoutes.Lands.path}
          customData={searchDone ? lands : tableLands}
          title=""
          hideTitle
          paginationDisabled
          mainFilter="Latest Lands"
          removeAll={true}
          totalItems={searchDone ? lands.length : tableLands.length}
          batch={FEATURED_LANDS}
          searchFilter={currentFilter === "createdAt" ? "all" : currentFilter}
          isLoading={liveLandsLoading}
        />
      </TableContainer>

      <Grid width="100%" flexDirection="column" display="flex" sx={{ backgroundColor: "#ccc" }}>
        <Trending
          sx={{
            position: "relative",
            borderRadius: "20px 20px 20px 20px",
            border: "none",
          }}
        >
          {!blogLoading && blogArticles["2"] && (
            <>
              <img
                src={blogArticles["2"].image}
                alt="Blog article"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  filter: "brightness(1)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(to right, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)",
                  zIndex: 1,
                }}
              />
              <BlogStoryBlock
                blog={blogArticles["2"]}
                fluid
                sx={{
                  zIndex: 2,
                }}
                search={blogArticles["2"].path || blogArticles["2"].Slug}
                subtitle={blogArticles["2"].subtitle}
                paragraph={blogArticles["2"].paragraph}
              />
            </>
          )}
        </Trending>
      </Grid>

      <TableContainer style={{ backgroundColor: "#ebe1dd", borderRadius: "20px 20px 20px 20px" }}>
        <FilterBlock
          sx={{
            padding: { xs: "18px", sm: "24px" },
            "@media (min-width: 600px)": {
              display: "flex",
              overflowX: "auto",
              whiteSpace: "nowrap",
            },
            "@media (max-width: 599px)": {
              flexDirection: "row",
            },
          }}
        >
          <ActionBtn
            disabled
            size="medium"
            sx={{
              "@media (max-width: 600px)": {
                size: "small",
                margin: 0.5,
              },
            }}
          >
            {isMobile ? "Latest Transactions" : "Latest Transactions"}
          </ActionBtn>
        </FilterBlock>
        <TableComponent
          schema={isMobile ? TransactionMobileSchema : TransactionsSchema}
          apiURL={`${ApiRoutes.ecoBank.transactions.root}?addToDetails=true`}
          batch={3}
          mobileTitle=""
          title=""
          totalItems={3}
          removeAll={false}
          hideTitle
          paginationDisabled
          mainFilter={isMobile ? "Latest" : "Latest Transactions"}
          link={AppRoutes.Transactions.path}
          // filter={{
          //   name: "eventType",
          //   paramName: "eventType",
          //   omitOnAll: true,
          //   values: [
          //     [
          //       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          //         <HandshakeOutlinedIcon sx={{ fontSize: "16px" }} /> sold
          //       </Box>,
          //       ["sale"],
          //     ],
          //     [
          //       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          //         <ListAltOutlinedIcon sx={{ fontSize: "16px" }} /> listed
          //       </Box>,
          //       ["listing"],
          //     ],

          //     [
          //       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          //         <EnergySavingsLeafOutlined sx={{ fontSize: "16px" }} /> mint
          //       </Box>,
          //       ["mint"],
          //     ],
          //   ],
          // }}
        />
      </TableContainer>

      <Grid width="100%" flexDirection="column" display="flex" sx={{ backgroundColor: "#ccc" }}>
        <Trending
          sx={{
            position: "relative",
            borderRadius: "20px 20px 20px 20px",
            border: "none",
          }}
        >
          {!blogLoading && blogArticles["5"] && (
            <>
              <img
                src={blogArticles["5"].image}
                alt="Blog article"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  filter: "brightness(1)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(to right, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)",
                  zIndex: 1,
                }}
              />
              <BlogStoryBlock
                blog={blogArticles["5"]}
                fluid
                sx={{
                  zIndex: 2,
                }}
                search={blogArticles["5"].path || blogArticles["5"].Slug}
                subtitle={blogArticles["5"].subtitle}
                paragraph={blogArticles["5"].paragraph}
              />
            </>
          )}
        </Trending>
      </Grid>

      {!isMobile && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ display: { md: "flex", sm: "none" }, alignItems: "center", justifyContent: "center" }}
        >
          <PopUpGraph onClose={handleClose} type={chartType} />
        </Modal>
      )}

      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          onOpen={handleMobileOpen}
          sx={{
            display: { xs: "block", sm: "none" },
            height: "100%",
            width: "100%",
            "& .MuiPaper-root": {
              backgroundColor: "transparent",
            },
          }}
        >
          <PopUpGraph onClose={handleClose} type={chartType} />
        </SwipeableDrawer>
      )}
    </LoadingScreen>
  );
};

const Block = styled("div")`
  width: 10px;
  height: 10px;
  border: 1px solid #2b3501;
  &.filled {
    background-color: #30af1a;
  }
`;

export const TableContainer = styled(Grid)`
  display: flex;
  flex-direction: column;
  align-items: start;
  // border-bottom: 1px solid;
  // border-color: ${({ theme }) => theme.palette.border.dark};
  background-color: #ccc;
  //height: 100%;
`;

const WidgetCard = styled(Grid)`
  display: flex;
  color: black;
  background: ${({ theme }) => theme.palette.back.green};
  border-top: 1px solid ${({ theme }) => theme.palette.border.dark};

  .BankWidget-value {
    font-size: 18px;
    line-height: 1.5;
    font-weight: 600;
    font-family: IBM Mono, monospace;
    @media (max-width: 992px) {
      font-size: 18px;
      font-weight: 600;
    }
  }
`;

export const BankWidget = (props: GridProps) => {
  return (
    <WidgetCard
      {...props}
      item
      xs={12}
      sm={6}
      md={4}
      sx={{
        p: sizing(6),
        ...props.sx,
      }}
    />
  );
};

export const Row = styled("div")`
  display: flex;
  flex-direction: row;
`;

export const BlockTitle = styled("div")`
  font-family: "IBM Plex Mono", monospace;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.3;
  text-transform: uppercase;
  color: black;
`;

export const WidgetGrid = styled(Grid)`
  //
`;
export const BackgroundDiv = styled("div")`
  background-color: transparent;
`;

export const SearchField = styled("div")`
  position: relative;
  color: #959a80;
  width: 100%;
  flex: 1;

  & > div:first-of-type {
    width: 100%;
  }

  input {
    width: 100%;
    color: #959a80;
    font-size: 16px;
    height: 40px;
    padding: 12px 0px;
    padding-right: 48px;
    box-sizing: border-box;

    @media (max-width: 560px) {
      font-size: 14px;
      padding: 10px 14px;
      padding-right: 44px;
    }

    &::placeholder {
      color: #959a80;
      opacity: 1;
    }
  }

  & .MuiOutlinedInput-root {
    height: 40px;
    border-radius: 20px;
    background-color: transparent;
    padding-right: 16px;

    @media (max-width: 560px) {
      padding-right: 32px;
    }

    & fieldset {
      border: 1px solid #959a80;
      border-radius: 20px;
    }

    &:hover fieldset {
      border-color: #959a80;
    }

    &.Mui-focused fieldset {
      border: 1px solid #959a80;
      border-color: #959a80;
    }

    & input {
      padding: 0 48px 0 16px;

      @media (max-width: 560px) {
        padding: 0 44px 0 14px;
        min-width: 98%;
      }
    }
  }

  & .MuiInputLabel-root {
    display: none;
  }
`;

export const TableLink = styled(NavLink)`
  text-decoration: none;
  text-align: center;
  display: block;
  transition: 0.5s all;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.primary};

  &:hover {
    opacity: 0.7;
    text-decoration: underline;
  }
`;

export const Trending = styled("div")`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(69vw - 0px);
  max-height: 600px;
  overflow: hidden;
  -webkit-transform: translateZ(0);

  // Below md the banner copy is fluid and padded, so let the banner grow to fit it rather than
  // clip or crowd it against the edges; 69vw (capped) stays the minimum.
  @media (max-width: 991.95px) {
    height: auto;
    max-height: none;
    min-height: min(69vw, 600px);
  }

  & > img {
    position: absolute;
    display: block;
    width: 100%; // Changed from auto
    height: 100%; // Changed from auto
    object-fit: cover; // Added to maintain aspect ratio
    object-position: center; // Added to center the image
    margin: 0;
    padding: 0;
  }
`;

export default EcoBank;
